import { useEffect } from "react";
import { type DecryptedItem } from "../vault/items";
import { isTauri, launchRdp, launchRdpAdhoc, launchRdpParams, openExternal } from "../desktop/bridge";
import { useSessions, type Launch } from "../vault/sessions";
import { launchFieldsOf } from "../vault/offline";
import { getRdpUseNative, getRestoreTabs, getOpenTabs, setOpenTabs } from "../vault/prefs";
import { useT } from "../i18n";

/**
 * Session-tab wiring plus the item-launch actions (SSH/RDP/URL) that sit on top of it: the
 * sessions store subscriptions, the desktop restore-open-tabs pair of effects, and the launch
 * helpers AppShell's grid/details/palette call. Flat return, same names as the original
 * component — see useVaultWorkspace for the contract.
 */
export function useSessionLaunch(readOnly: boolean) {
  const t = useT();
  const desktop = isTauri();
  const sessionTabs = useSessions((s) => s.tabs);
  const activeTab = useSessions((s) => s.activeKey);
  const openSession = useSessions((s) => s.open);
  const closeSession = useSessions((s) => s.close);
  const closeAllSessions = useSessions((s) => s.closeAll);
  const setActiveTab = useSessions((s) => s.setActive);

  // Restore-open-tabs (desktop comfort). Persist the item-backed session tabs (IDs only — never
  // names/secrets, §20) so they reopen next launch. Skip empty writes so a lock/sign-out teardown
  // (closeAll → []) doesn't wipe the saved set; AppShell remounts on the next unlock and restores.
  useEffect(() => {
    if (!desktop || !getRestoreTabs()) return;
    const restorable = sessionTabs
      .filter((tb) => tb.launch?.mode === "item" && (tb.kind === "ssh" || tb.kind === "rdp"))
      .map((tb) => ({ itemId: (tb.launch as { mode: "item"; itemId: string }).itemId, kind: tb.kind }));
    if (restorable.length) setOpenTabs(restorable);
  }, [sessionTabs, desktop]);

  // AppShell mounts only when unlocked, so reopen the saved tabs once on mount. Item-mode launches
  // reconnect via the native session (itemId → native decrypt), so no org needs loading; the brief
  // delay lets initSession (private key → native) settle before the panes launch. Titles are generic
  // because item names aren't persisted.
  useEffect(() => {
    if (!desktop || !getRestoreTabs()) return;
    const saved = getOpenTabs();
    if (!saved.length) return;
    const id = window.setTimeout(() => {
      for (const ref of saved) {
        if (ref.kind === "ssh" || ref.kind === "rdp") {
          openSession({ kind: ref.kind, title: ref.kind.toUpperCase(), launch: { mode: "item", itemId: ref.itemId } });
        }
      }
    }, 500);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot restore per unlock (mount)
  }, []);

  /** The item's primary URL (for website/login "Open"). */
  function urlOf(it: DecryptedItem): string | undefined {
    return it.content.fields.find((f) => (f.key === "url" || f.type === "url") && f.value)?.value;
  }

  function canLaunch(it: DecryptedItem): boolean {
    // SSH/RDP/network_device are launchable; network_device is the clientless web-admin (Phase G).
    return it.type === "ssh" || it.type === "rdp" || it.type === "network_device" || !!urlOf(it);
  }

  /** Launch an SSH/RDP item. SSH opens an in-app tabbed session; RDP launches the platform's
   *  native client in its own OS window (gateway-capable — there is no embedded RDP). On web both
   *  show a "download the desktop app" placeholder. When offline / after a mid-session connectivity
   *  drop, launch from the already-decrypted fields (params) so it never needs the server; online
   *  uses the native item path (re-decrypts in Rust, audits). */
  function openItemSession(it: DecryptedItem) {
    // Web-admin console (network_device): browser-isolation, web-only. No desktop native path.
    if (it.type === "network_device") {
      if (desktop || readOnly) {
        openSession({ kind: "placeholder", itemType: "rdp", title: it.content.name });
      } else {
        const connectorId = launchFieldsOf(it)["connector"] || undefined;
        openSession({ kind: "brokered-web", title: it.content.name, launch: { mode: "item", itemId: it.id }, connectorId });
      }
      return;
    }
    const kind = it.type === "ssh" ? "ssh" : "rdp";
    if (!desktop) {
      // Web build: online → clientless brokered session (Lane B / jump server). Offline (read-only,
      // no server to mint a token) → keep the download-the-app placeholder.
      if (readOnly) {
        openSession({ kind: "placeholder", itemType: kind, title: it.content.name });
      } else {
        // Route through the item's connector override (blank = org default / direct, resolved server-side).
        const connectorId = launchFieldsOf(it)["connector"] || undefined;
        openSession({
          kind: kind === "ssh" ? "brokered-ssh" : "brokered-rdp",
          title: it.content.name,
          launch: { mode: "item", itemId: it.id },
          connectorId,
        });
      }
      return;
    }
    if (kind === "rdp") {
      const fields = launchFieldsOf(it);
      // Renderer precedence: the per-item "RDP client" field wins; otherwise the global setting
      // (default = embedded in-app viewer, with a one-click native fallback on connect failure).
      const choice = fields["rdp_client"];
      const embedded =
        choice === "Embedded (in-app)"
          ? true
          : choice === "Native OS client"
            ? false
            : !getRdpUseNative();
      if (embedded) {
        openSession({
          kind: "rdp",
          title: it.content.name,
          launch: readOnly ? { mode: "rdp-fields", fields, itemId: it.id } : { mode: "item", itemId: it.id },
        });
      } else {
        // External native client — not an app tab.
        const p = readOnly ? launchRdpParams(fields, it.id) : launchRdp(it.id);
        void p.catch((e) => window.alert(`${t("RDP launch failed")}: ${e instanceof Error ? e.message : String(e)}`));
      }
      return;
    }
    if (readOnly) {
      openSession({ kind: "ssh", title: it.content.name, launch: { mode: "ssh-fields", fields: launchFieldsOf(it), itemId: it.id } });
    } else {
      openSession({ kind: "ssh", title: it.content.name, launch: { mode: "item", itemId: it.id } });
    }
  }

  /** Re-open an embedded RDP tab's connection in the native OS client (MFA / full-fidelity escape). */
  function nativeFromLaunch(launch: Launch) {
    const p =
      launch.mode === "rdp-fields"
        ? launchRdpParams(launch.fields, launch.itemId)
        : launch.mode === "adhoc-rdp"
          ? launchRdpAdhoc(launch.params)
          : launch.mode === "item"
            ? launchRdp(launch.itemId)
            : Promise.resolve();
    void p.catch((e) => window.alert(`${t("RDP launch failed")}: ${e instanceof Error ? e.message : String(e)}`));
  }

  function launchLabel(it: DecryptedItem): string {
    if (it.type === "ssh") return t("Launch SSH");
    if (it.type === "rdp") return t("Launch RDP");
    if (it.type === "network_device") return t("Open web admin");
    return t("Open");
  }

  /** Run the item's default action; returns false if there is nothing launchable. */
  function launchItem(it: DecryptedItem): boolean {
    if (it.type === "ssh" || it.type === "rdp" || it.type === "network_device") {
      openItemSession(it);
      return true;
    }
    const u = urlOf(it);
    if (u) {
      void openExternal(u);
      return true;
    }
    return false;
  }

  return {
    desktop,
    sessionTabs,
    activeTab,
    openSession,
    closeSession,
    closeAllSessions,
    setActiveTab,
    urlOf,
    canLaunch,
    openItemSession,
    nativeFromLaunch,
    launchLabel,
    launchItem,
  };
}
